import { IcFlightPlan, IcFlightPlanFill } from '@assets/icons/40x40';
import { HStack, Icon, Tabs, Text, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { FormattedMessage, useIntl } from 'react-intl';

import { FlightPlanInfoData, PopupPositionData } from '@/types/popup.types';
import { FlightPlanActivationStateEnum } from '@/types/utm/flightPlan.types';

import FlightPlanDetails from './FlightPlanDetails';
import FlightPlanDrones from './FlightPlanDrones';
import FlightPlanInstructions from './FlightPlanInstructions';
import FlightPlanPilots from './FlightPlanPilots';

export default function FlightPlanInfo({
  mapConstraintsRef,
  featureValue,
  activation,
  top,
  left,
  onClose,
}: Readonly<FlightPlanInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();
  const flightPlan = featureValue;
  const displayedActivation = flightPlan.activations.find((a) => a.id === activation?.id) ?? activation;

  const isActive = displayedActivation?.state === FlightPlanActivationStateEnum.ACTIVE;
  const color = isActive ? 'green' : 'neutral.300';
  const missionId = flightPlan.externalId ?? flightPlan.name;

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} key={flightPlan.code} top={top} left={left} offsetY={-100}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="flightPlanInfos.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '416px', '4k': '612px' }} minHeight={610}>
        <VStack gap={0.25} width="100%" height="100%" backgroundColor="neutral.900">
          <HStack
            width="100%"
            height={{ base: '80px', '4k': '120px' }}
            gap={2}
            backgroundColor="neutral.700"
            justifyContent="flex-start"
            paddingLeft={3}
          >
            <Icon
              as={isActive ? IcFlightPlanFill : IcFlightPlan}
              boxSize={{ base: '32px', '4k': '40px' }}
              color={color}
            />
            <VStack gap={0.5} alignItems="start">
              <Text padding={0.5} backgroundColor="neutral.black">
                {missionId}
              </Text>
              <Text color={color}>
                <FormattedMessage
                  id={
                    displayedActivation?.state
                      ? `flightPlan.activation.state.${displayedActivation.state}`
                      : `flightPlan.state.${flightPlan.activations[0].state}`
                  }
                />
              </Text>
            </VStack>
          </HStack>
          <Tabs.Root maxHeight="684px" size="screenStd" variant="neutral" gap={0.25} lazyMount defaultValue="details">
            <Tabs.List>
              <Tabs.Trigger value="details">
                <FormattedMessage id="flightPlanInfos.tab.details.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value="instructions">
                <FormattedMessage id="flightPlanInfos.tab.instructions.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value="drones">
                <FormattedMessage id="flightPlanInfos.tab.drones.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value="pilots">
                <FormattedMessage id="flightPlanInfos.tab.pilots.title" />
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.ContentGroup>
              <Tabs.Content value="details">
                <FlightPlanDetails
                  startDate={displayedActivation?.startDate}
                  endDate={displayedActivation?.endDate}
                  maxAltitude={flightPlan.altitudeMax}
                  mission={missionId}
                  provider={flightPlan.provider}
                  missionStartTime={flightPlan.startTime}
                  missionEndTime={flightPlan.endTime}
                  information={[]}
                />
              </Tabs.Content>
              <Tabs.Content value="instructions">
                <FlightPlanInstructions instruction={formatMessage({ id: 'flightPlanInfos.tab.instructions.empty' })} />
              </Tabs.Content>
              <Tabs.Content value="drones">
                <FlightPlanDrones drones={flightPlan.drones} />
              </Tabs.Content>
              <Tabs.Content value="pilots">
                <FlightPlanPilots pilots={flightPlan.drones.flatMap((drone) => drone.pilots)} />
              </Tabs.Content>
            </Tabs.ContentGroup>
          </Tabs.Root>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
